import {
	AsyncPipe,
	Location,
	NgFor,
	NgIf,
	NgTemplateOutlet,
} from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import {
	MatMenu,
	MatMenuContent,
	MatMenuItem,
	MatMenuTrigger,
} from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink, RouterModule, RouterOutlet } from '@angular/router';
import { CurrentUser } from '@core/domain/current-user';
import { InstanceModule } from '@core/domain/instance-module';
import { ModuleInstanceType, ModuleRole } from '@core/domain/module-role';
import { Organization } from '@core/domain/organization';
import { AppSelector, navigateToMyAccount } from '@core/store';
import { DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppIcons,
	AvatarEntity,
	DcAvatarModule,
	DcLayoutModule,
} from '@datachain/ui-sdk/components';
import {
	DcArrayPipeModule,
	DcTruncatePipeModule,
} from '@datachain/ui-sdk/pipes';
import { Store } from '@ngrx/store';
import { DcIcons } from '@ui/app-console.icons';
import { SplashComponent } from '@ui/components/splash/splash.component';
import { Map } from 'immutable';
import {
	map,
	Observable,
	scan,
	shareReplay,
	takeUntil,
	tap,
	withLatestFrom,
} from 'rxjs';

import {
	ActionIconsInjectable,
	CsIconsInjectable,
	UiComponentIconsInjectable,
} from './app.config';
import { Feature, routes } from './app.routes';

type UserAction = {
	description: string;
	actionName: string;
	icon: string;
	action: (...args: Array<unknown>) => void;
};

interface INavLink {
	label: string;
	path: string;
	iconName: string | undefined;
}

const directives = [
	NgIf,
	NgFor,
	RouterOutlet,
	RouterLink,
	AsyncPipe,
	RouterModule,
];
const modules = [
	FormsModule,
	ReactiveFormsModule,
	NgTemplateOutlet,
	MatToolbarModule,
	MatIconModule,
	MatIconButton,
	MatListModule,
	MatMenuItem,
	MatMenuContent,
	MatMenuTrigger,
	MatMenu,
	MatTooltip,
	MatDividerModule,
	DcLayoutModule,
	DcAvatarModule,
	DcArrayPipeModule,
	DcTruncatePipeModule,
];

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	standalone: true,
	imports: [...directives, ...modules, SplashComponent],
})
export class AppComponent extends DcBaseComponent implements OnInit {
	protected readonly DcIcons = DcIcons;
	public title = 'Dc-Console';
	public showSplash = true;
	public navLinks: Array<INavLink> = [];

	public readonly profileActions: Array<UserAction> = [
		{
			actionName: 'my account',
			description: $localize`:i18n=@@feature.account:`,
			icon: DcIcons.Account,
			action: (): void => {
				this.store.dispatch(navigateToMyAccount());
			},
		},
		{
			actionName: 'logout',
			description: $localize`:i18n=@@general.logout:`,
			icon: DcIcons.Logout,
			action: (): void => {
				window.location.href = this.location.prepareExternalUrl('/logout');
			},
		},
	];

	public vo$: Observable<{
		userAvatar: AvatarEntity | null;
		userAvailableInstances: Map<string, InstanceModule>;
	}>;

	protected readonly ModuleInstanceType = ModuleInstanceType;
	public constructor(
		private readonly store: Store,
		private readonly location: Location,
		private readonly appSelector: AppSelector,
		@Inject(CsIconsInjectable) private readonly dcIcons: AppIcons,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		@Inject(UiComponentIconsInjectable)
		private readonly uiComponentIcons: AppIcons,
	) {
		super();
		this.cmpId = 'app-component';

		this.dcIcons.addIconsToRegistry();
		this.actionIcons.addIconsToRegistry();
		this.uiComponentIcons.addIconsToRegistry();

		const data$ = this.appSelector.getCurrentUser$().pipe(
			takeUntil(this.onDestroy$),
			withLatestFrom(this.appSelector.getCurrentOrganization$()),
			tap(([user, organisation]) => {
				this.navLinks = this.generateNavLinks(user, organisation);
			}),
			scan(
				(state, [user]) => ({
					avatar: AvatarEntity.build(user.userName),
					instances: user.availableInstanceModules,
				}),
				{
					avatar: {} as AvatarEntity,
					instances: Map<string, InstanceModule>(),
				},
			),
			shareReplay(1),
		);

		this.vo$ = data$.pipe(
			map((data) => ({
				userAvatar: data.avatar,
				userAvailableInstances: data.instances,
			})),
		);
	}

	public override ngOnInit(): void {
		super.ngOnInit();
	}

	public logout(): void {
		window.location.href = this.location.prepareExternalUrl('/logout');
	}

	public onSplashDismissed(): void {
		this.showSplash = false;
	}

	private generateNavLinks(
		user: CurrentUser,
		organisation: Organization,
	): Array<INavLink> {
		return routes
			.filter((route) => {
				const hasNav = route && route.data && route.data['isPrincipalNav'];
				const hasAccess =
					route &&
					route.data &&
					route.data['roles'] &&
					route.data['roles'].some((role: ModuleRole) =>
						user.accounts.get(organisation.id)?.includes(role),
					);
				return hasNav && hasAccess;
			})
			.map((r) => {
				let label = 'unnamed';
				if (r.data && r.data['featureName']) {
					label = this.getFeatureName(r.data['featureName']);
				}
				return {
					label,
					path: r.path ? `/organization/${organisation.id}/${r.path}` : '',
					iconName:
						r.data && r.data['iconName'] ? r.data['iconName'] : undefined,
				};
			});
	}

	private getFeatureName(feat: Feature): string {
		switch (feat) {
			case 'modules':
				return $localize`:i18n=@@feature.modules:`;
			case 'users':
				return $localize`:i18n=@@feature.users:`;
			case 'groups':
				return $localize`:i18n=@@feature.groups:`;
			case 'admin':
				return $localize`:i18n=@@feature.admin:`;
		}
	}
}
